import { useEffect, useMemo, useState } from "react";
import { ExhibitionInputSchema } from "@yushi-portfolio/api-types";
import type { ExhibitionDocument } from "@yushi-portfolio/content-schema";
import { LocalizedInput, Select, TextInput } from "../components/fields";
import { PublishButton } from "../components/PublishControls";
import type { ApiClient } from "../lib/api";
import { describeError, useUnsavedGuard, validationErrors } from "../lib/editor";
import { emptyExhibitionForm, exhibitionFromForm, exhibitionToForm, type ExhibitionForm } from "../lib/forms";
import { navigate } from "../lib/router";

const TYPES = [
  { value: "solo", label: "個展 solo" },
  { value: "group", label: "グループ展 group" },
  { value: "screening", label: "上映 screening" },
  { value: "event", label: "イベント event" },
  { value: "other", label: "その他 other" },
] as const;

export function ExhibitionEditor({ api, id }: { api: ApiClient; id?: string }) {
  const [form, setForm] = useState<ExhibitionForm>();
  const [saved, setSaved] = useState<string>();
  const [published, setPublished] = useState<ExhibitionDocument>();
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<{ message: string; details: string[] }>();
  const [notice, setNotice] = useState<string>();

  useEffect(() => {
    setErrors(undefined);
    if (!id) {
      const empty = emptyExhibitionForm();
      setForm(empty);
      setSaved(JSON.stringify(exhibitionFromForm(empty)));
      return;
    }
    api.getExhibition(id).then(
      (res) => {
        const next = exhibitionToForm(res.draft);
        setForm(next);
        setSaved(JSON.stringify(exhibitionFromForm(next)));
        setPublished(res.published);
      },
      (e) => setErrors(describeError(e)),
    );
  }, [id]);

  const body = useMemo(() => (form ? exhibitionFromForm(form) : undefined), [form]);
  const dirty = body !== undefined && JSON.stringify(body) !== saved;
  useUnsavedGuard(dirty);

  if (!form || !body) return errors ? <div className="notice error">{errors.message}</div> : <p className="muted">読み込み中…</p>;
  const set = (patch: Partial<ExhibitionForm>) => setForm({ ...form, ...patch });

  async function save() {
    if (!body) return;
    setNotice(undefined);
    const clientErrors = validationErrors(ExhibitionInputSchema, body);
    if (clientErrors.length) {
      setErrors({ message: "入力内容を確認してください。", details: clientErrors });
      return;
    }
    setBusy(true);
    try {
      if (!id) {
        const res = await api.createExhibition(body);
        setSaved(JSON.stringify(body));
        navigate(`/exhibitions/${encodeURIComponent(res.draft.id)}`);
        return;
      }
      const res = await api.updateExhibition(id, body);
      const next = exhibitionToForm(res.draft);
      setForm(next);
      setSaved(JSON.stringify(exhibitionFromForm(next)));
      setErrors(undefined);
      setNotice("下書きを保存しました。");
    } catch (e) {
      setErrors(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <header className="page-head sticky">
        <div>
          <a href="#/exhibitions" className="muted">
            ← 展覧会一覧
          </a>
          <h1>{form.title.ja || "新しい展覧会"}</h1>
          <p className="muted">
            {published ? <span className="badge published">公開中</span> : <span className="badge">未公開</span>} {id && <span className="mono">{id}</span>}{" "}
            {dirty && <span className="badge warn">未保存</span>}
          </p>
        </div>
        <div className="actions">
          <button type="button" className="primary" disabled={busy || (!dirty && Boolean(id))} onClick={save}>
            {busy ? "保存中…" : "Save Draft"}
          </button>
          {id && (
            <PublishButton
              disabledReason={dirty ? "保存してから公開してください" : undefined}
              publish={() => api.publishExhibition(id)}
              onPublished={() => api.getExhibition(id).then((res) => setPublished(res.published), () => undefined)}
            />
          )}
        </div>
      </header>
      {notice && <div className="notice ok">{notice}</div>}
      {errors && (
        <div className="notice error" role="alert">
          <strong>{errors.message}</strong>
          {errors.details.length > 0 && (
            <ul>
              {errors.details.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="stack">
        <LocalizedInput label="タイトル" required value={form.title} onChange={(title) => set({ title })} />
        <div className="grid-2">
          <Select label="種類" value={form.type} options={TYPES} onChange={(type) => set({ type })} />
          <TextInput label="slug (任意)" value={form.slug} onChange={(slug) => set({ slug })} />
        </div>
        <div className="grid-3">
          <TextInput label="会場" value={form.venue} onChange={(venue) => set({ venue })} />
          <TextInput label="都市" value={form.city} onChange={(city) => set({ city })} />
          <TextInput label="国" value={form.country} onChange={(country) => set({ country })} />
        </div>
        <div className="grid-2">
          <TextInput label="開始日" value={form.startDate} placeholder="2025 / 2025-05 / 2025-05-10" onChange={(startDate) => set({ startDate })} />
          <TextInput label="終了日" value={form.endDate} placeholder="2025-06-22" onChange={(endDate) => set({ endDate })} />
        </div>
        <TextInput label="URL" inputMode="url" value={form.url} placeholder="https://" onChange={(url) => set({ url })} />
      </div>
    </section>
  );
}
